import { CSSProperties, FC, useMemo } from 'react';

import { getPersonalIdentifierForms } from '@/entities/personalIdentifiers/helpers/get-forms';
import { FormsByCollection } from '@/shared/lib/hooks/getPartialForm';
import { CollectionName, CollectionToDtoMap } from '@/shared/lib/sj-orm/types';
import { useTranslation } from '@/shared/utils/i18n/useTranslation';

import { isColDto } from '../../helpers';
import { ItemBaseDto } from '../../types';
import { ItemStatus } from '../ItemStatus';

import { itemRenderers } from './helpers';

type ColumnOptions<T> = {
  title?: string;
  render: FC<{ data: T; updateFields: string[] }>;
  style?: CSSProperties;
};

const colCommonStyle = {
  paddingInline: 16,
  width: 178,
  maxWidth: 178,
  overflow: 'hidden',
};
const colStatusStyle: CSSProperties = {
  width: 40,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
};

export const useColumns = ({
  collectionName,
  filledItems,
}: {
  collectionName: CollectionName;
  filledItems: Record<string, ItemBaseDto>;
}) => {
  const { t } = useTranslation(['forms_assets']);

  return useMemo(() => {
    const renderers = itemRenderers[collectionName];

    if (!renderers) throw new Error('Unsupported collection');

    const columns: ColumnOptions<CollectionToDtoMap[typeof collectionName]>[] = [
      {
        title: '',
        render: ({ data }) => <ItemStatus status={Boolean(filledItems[data.id])} />,
        style: colStatusStyle,
      },
      {
        title: 'Missing data',

        render: ({ data, updateFields }) => {
          let form;

          if (isColDto(CollectionName.ASSETS, data)) {
            form = FormsByCollection[CollectionName.ASSETS][data.category];
          }
          if (isColDto(CollectionName.CONTACTS, data)) {
            form = FormsByCollection[CollectionName.CONTACTS][data.type];
          }
          if (isColDto(CollectionName.BENEFICIARIES, data)) {
            form = FormsByCollection[CollectionName.BENEFICIARIES];
          }
          if (isColDto(CollectionName.PERSONAL_IDENTIFIERS_DOCUMENTS, data)) {
            form = getPersonalIdentifierForms(data.personalIdentifierType);
          }
          // if (isColDto(CollectionName.PRIVATE_DOCUMENTS, data)) {
          //   form = FormsByCollection[CollectionName.PRIVATE_DOCUMENTS][DocumentType.PRIVATE_DOCUMENTS];
          // }
          if (!form) {
            return <></>;
          }

          // @ts-ignore
          const labels = updateFields.map((updateField) => form[0]?.[updateField]?.label);
          const texts = labels
            .filter((el) => el !== undefined)
            .map((l) => t(l))
            .join(', ');

          return <>{texts}</>;
        },
        style: colCommonStyle,
      },
      {
        title: 'Title',
        // @ts-ignore
        render: renderers.Title,
        style: colCommonStyle,
      },
      {
        title: 'Category',
        // @ts-ignore
        render: renderers.Category,
        style: colCommonStyle,
      },
      {
        title: 'Country',
        // @ts-ignore
        render: renderers.Country,
        style: colCommonStyle,
      },
    ];

    return columns;
  }, [collectionName, filledItems]);
};
